import {
  Button,
  Flex,
  Heading,
  Spinner,
  Text,
  TextInput,
} from "@instructure/ui";
import { StatusCodes } from "http-status-codes";
import { t } from "i18next";
import React, { useRef, useState } from "react";
import { CourseService } from "../../services/CourseService";
import { SubjectService } from "../../services/SubjectService";
import { ERROR, useNotification } from "../CustomHooks/NotificationProvider";
import { Selector } from "../Selectors/Selector";

const DepartmentSection = (props) => {
  const { setOpenSection, department = {}, upsertHandler } = props;
  const showNotification = useNotification();
  const [creatingNewDepartmentInProgress, setCreatingNewDepartmentInProgress] =
    useState(false);
  const [currentDepartment, setCurrentDepartment] = useState({ ...department });
  const [shortNameError, setShortNameError] = useState(null);

  const handleDepartmentUpsert = () => {
    if (creatingNewDepartmentInProgress) {
      return;
    }
    if (!currentDepartment?.title) {
      setShortNameError(t("academicHierarchy.emptyDepartmentShortName"));
      return;
    }

    setCreatingNewDepartmentInProgress(true);

    SubjectService.upsertDepartment(currentDepartment)
      .then((updatedDepartment) => {
        setOpenSection(false);
        if (upsertHandler) {
          upsertHandler(updatedDepartment);
        }
      })
      .catch((e) => {
        if (e.status === StatusCodes.BAD_REQUEST) {
          setShortNameError(
            t("academicHierarchy.departmentShortNameAlreadyExists")
          );
        } else {
          const notificationMessage = currentDepartment.ddl_id
            ? "notifications.general.updateError"
            : "notifications.general.createError";
          showNotification(
            t(notificationMessage, { obj: t("notifications.department.obj") }),
            ERROR
          );
        }
      })
      .finally(() => {
        setCreatingNewDepartmentInProgress(false);
      });
  };

  return (
    <>
      <Heading as="h4" level="h4" margin="0 0 small 0">
        <Text size="small">
          {currentDepartment?.ddl_id
            ? t("academicHierarchy.edit")
            : t("academicHierarchy.createNew")}{" "}
          {t("academicHierarchy.department")}
        </Text>
      </Heading>

      <Flex as="form" gap="small" wrap="wrap">
        <Flex.Item width="100%">
          <TextInput
            size="small"
            renderLabel={
              <Text size="small">{t("academicHierarchy.name")}</Text>
            }
            placeholder={"Ex.: Mathematics"}
            value={currentDepartment.name ? currentDepartment.name : ""}
            onChange={(event) => {
              setCurrentDepartment((prevDepartment) => ({
                ...prevDepartment,
                name: event.target.value,
              }));
            }}
          />
        </Flex.Item>
        <Flex.Item width="100%">
          <TextInput
            size="small"
            renderLabel={
              <Text size="small">{t("academicHierarchy.code")}</Text>
            }
            placeholder={"Ex.: MATH"}
            value={currentDepartment.title ? currentDepartment.title : ""}
            onChange={(event) => {
              if (event.nativeEvent.data !== " ") {
                setCurrentDepartment((prevDepartment) => ({
                  ...prevDepartment,
                  title: event.target.value,
                }));
              }
            }}
            messages={
              shortNameError ? [{ text: shortNameError, type: "error" }] : []
            }
            interaction={!currentDepartment.ddl_id ? "enabled" : "disabled"}
          />
        </Flex.Item>
        <Flex.Item textAlign="end" shouldGrow>
          <Button
            size="small"
            color="primary"
            onClick={handleDepartmentUpsert}
            disabled={creatingNewDepartmentInProgress}
          >
            {creatingNewDepartmentInProgress ? (
              <Spinner renderTitle={t("global.pleaseWait")} size="x-small" />
            ) : (
              (currentDepartment?.ddl_id
                ? t("academicHierarchy.edit")
                : t("academicHierarchy.create")) +
              " " +
              t("academicHierarchy.department")
            )}
          </Button>
        </Flex.Item>
      </Flex>
    </>
  );
};

const ContextSection = ({
  setOpenSection,
  setFilteredCourses,
  setSelectedCourses,
  availableDepartments,
}) => {
  const showNotification = useNotification();
  const [loading, setLoading] = useState(false);
  const [nameError, setNameError] = useState(null);
  const [codeError, setCodeError] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const nameRef = useRef();
  const codeRef = useRef();

  const handleCreateContext = () => {
    if (!nameRef.current.value) {
      setNameError(t("academicHierarchy.emptyContextName"));
      return;
    }
    setLoading(true);
    CourseService.upsertContext(
      nameRef.current.value,
      codeRef.current.value,
      selectedDepartment.title
    )
      .then((context) => {
        setFilteredCourses((prevList) => [...prevList, context]);
        setSelectedCourses((prevList) => [...prevList, context]);
        setOpenSection(false);
        showNotification(
          t("notifications.general.addSuccess", {
            obj: t("notifications.context.obj"),
          })
        );
      })
      .catch((e) => {
        if (e.status === StatusCodes.BAD_REQUEST) {
          setCodeError(t("academicHierarchy.contextCodeAlreadyExists"));
        }
        showNotification(
          t("notifications.general.createError", {
            obj: t("notifications.context.obj"),
          }),
          ERROR
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleDepartmentSelect = (departmentId) => {
    setSelectedDepartment(
      availableDepartments.find((dep) => dep.id === departmentId)
    );
  };

  return (
    <>
      <Flex as="form" gap="small" wrap="wrap">
        <Flex.Item>
          <Selector
            id="department-select"
            label={t("academicHierarchy.createContextFor")}
            options={availableDepartments}
            selectHandler={handleDepartmentSelect}
            size="small"
          />
        </Flex.Item>
        <Flex.Item width="100%">
          <TextInput
            size="small"
            renderLabel={
              <Text size="small">{t("academicHierarchy.name")}</Text>
            }
            ref={nameRef}
            messages={nameError ? [{ text: nameError, type: "error" }] : []}
            placeholder={t("academicHierarchy.contextCreationNamePlaceholder")}
          />
        </Flex.Item>
        <Flex.Item width="100%">
          <TextInput
            size="small"
            renderLabel={
              <Text size="small">{t("academicHierarchy.code")}</Text>
            }
            ref={codeRef}
            messages={codeError ? [{ text: codeError, type: "error" }] : []}
            placeholder={t("academicHierarchy.contextCreationCodePlaceholder")}
          />
        </Flex.Item>
        <Flex.Item textAlign="end" shouldGrow>
          <Button
            size="small"
            color="primary"
            onClick={handleCreateContext}
            interaction={
              selectedDepartment && !loading ? "enabled" : "disabled"
            }
          >
            {loading ? (
              <Spinner renderTitle={t("global.pleaseWait")} size="x-small" />
            ) : (
              t("academicHierarchy.addContext")
            )}
          </Button>
        </Flex.Item>
      </Flex>
    </>
  );
};

export { ContextSection, DepartmentSection };
